import { useTranslation } from 'react-i18next';

import { Avatar, Badge, Circle, HStack, Spacer, Text, VStack } from '@bonusly/design-system';

import type { BizyPeopleBrowserEntry } from '../../../lib/bizy/types';

// Reusable, presentational "Bizy noticed" list (BIZY-636). Renders the condensed
// People Browser rows — avatar + unread blip, name, 2-line headline, and a
// "Fading soon" badge. Seen (read) entries are dimmed. Intentionally headless of
// data/navigation so the same component backs the compact web menu and the
// fullscreen sidebar today, and the desktop sidebar later (BIZY-642).

// The sidebar shows at most 5 recent entries (backend also caps this).
const MAX_ENTRIES = 5;

export interface BizyPeopleListProps {
	entries: BizyPeopleBrowserEntry[];
	/** Invoked when a row is activated (e.g. to open the person / dive in). */
	onSelectPerson?: (entry: BizyPeopleBrowserEntry) => void;
	/**
	 * Observed-moment id to visually highlight. Keyed on the moment (not the
	 * person) so that when a person has multiple entries only the selected one
	 * highlights. Takes precedence over `focusedPersonId`.
	 */
	selectedObservedMomentId?: string | null;
	/**
	 * Person id to highlight when no specific moment is selected — used by the
	 * notification-peek path, which points at a person (not a single moment).
	 */
	focusedPersonId?: string | null;
}

export function BizyPeopleList({ entries, onSelectPerson, selectedObservedMomentId, focusedPersonId }: BizyPeopleListProps) {
	const { t } = useTranslation('admin_bizy');

	if (entries.length === 0) return null;

	const hasSelectedMoment = selectedObservedMomentId != null;

	return (
		<VStack data-component="bizy-people-list" align="stretch" spacing={1}>
			{entries.slice(0, MAX_ENTRIES).map((entry) => {
				// A specific selected moment wins; otherwise fall back to the peek's
				// person-level highlight.
				const isFocused = hasSelectedMoment
					? selectedObservedMomentId === entry.observedMomentId
					: focusedPersonId != null && focusedPersonId === entry.person.id;
				const interactive = Boolean(onSelectPerson);

				return (
					<HStack
						key={entry.observedMomentId}
						as={interactive ? 'button' : 'div'}
						align="flex-start"
						spacing={2.5}
						w="100%"
						textAlign="left"
						px={2}
						py={2}
						borderRadius="md"
						bg={isFocused ? 'secondary.bgAlpha' : 'transparent'}
						_hover={interactive ? { bg: 'secondary.bgAlphaHover' } : undefined}
						cursor={interactive ? 'pointer' : 'default'}
						transition="background-color 150ms ease"
						onClick={interactive ? () => onSelectPerson?.(entry) : undefined}
						data-unread={entry.unread ? 'true' : 'false'}
						data-selected={isFocused ? 'true' : 'false'}
					>
						<Avatar name={entry.person.name ?? undefined} src={entry.person.avatarUrl ?? undefined} boxSize="24px" flexShrink={0} />
						<VStack align="stretch" spacing={0.5} minW={0} flex={1}>
							{/* Name + unread blip share one line; the preview below spans that width. */}
							<HStack spacing={2} align="center">
								<Text size="md" color={entry.unread ? 'secondary.fg' : 'text.softest'} noOfLines={1} flex={1}>
									{entry.person.name}
								</Text>{' '}
								{entry.fadingSoon && (
									<Badge colorScheme="red" fontSize="xs" textTransform="none" fontWeight="medium" borderRadius="full" px={2} py="1px">
										{t('menu.fadingSoon')}
									</Badge>
								)}
								{entry.unread && (
									<>
										<Spacer />
										<Circle size="8px" bg="primary.fg" flexShrink={0} aria-label={t('menu.unreadIndicator')} data-testid="bizy-people-unread-blip" />
									</>
								)}
							</HStack>
							<Text fontSize="xs" color="text.softest" noOfLines={2}>
								{entry.headline}
							</Text>
						</VStack>
					</HStack>
				);
			})}
		</VStack>
	);
}
